import React from 'react';
import {Redirect, Route, Switch, useLocation} from 'wouter';

import {DocumentsRoute} from './application/document-workflow/index-route';
import {ManageUsersPage} from './application/manage-users-page';
import {ProfilePage} from './application/profile-page';
import {QueryWorkflow} from './application/query-workflow';
import {QueryManagementPanel} from './application/query-workflow/query-management-panel/query-management-panel';
import {QueryWebsocketEventsManager} from './application/query-workflow/websocket-events-manager/query-websocket-events-manager';
import {AppHeader} from './components/layout/application-header/application-header';
import {LocalErrorBoundary} from './components/error-boundary';
import {useUrlSearchValue, UrlSearchParam} from './hooks/use-search-params';
import {useIsCanManageQueries, useIsCanManageUsers} from './hooks/user/use-has-permission';
import {useGlobalVersion} from './providers/version/use-global-version';
import {GVManagementPanel} from './application/gv-management-panel/gv-management-panel';
import {IconGallery} from './components/icon-gallery/icon-gallery';

/**
 * Checks if a path requires full page navigation instead of client-side routing.
 * OAuth and public API paths are served by the gateway, not by the SPA
 */
function needsFullPageReload(path: string): boolean {
    return path.startsWith('/oauth2/') || path.startsWith('/api/');
}

export function AuthenticatedApp() {
    const [location, navigate] = useLocation();

    const urlSearchValue = useUrlSearchValue(UrlSearchParam.originUrl);
    const isCanManageUsers = useIsCanManageUsers();
    const isCanManageQueries = useIsCanManageQueries();
    const globalVersionProps = useGlobalVersion({
        // if the user is on GV management page, they want their data to be updated in realtime to
        // see validation status changes
        shouldRefetchGVFrequently: location === '/global-versions'
    });

    React.useEffect(() => {
        if (urlSearchValue.value && urlSearchValue.value !== '/') {
            if (needsFullPageReload(urlSearchValue.value)) {
                window.location.href = urlSearchValue.value;
            } else {
                navigate(urlSearchValue.value, {replace: true});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <AppHeader />

            <LocalErrorBoundary>
                <Switch>
                    <Route path="/queries" nest={true}>
                        <QueryWorkflow globalVersionProps={globalVersionProps} />
                    </Route>

                    <Route path="/documents" nest={true}>
                        <DocumentsRoute globalVersionProps={globalVersionProps} />
                    </Route>

                    <Route path="/profile" component={ProfilePage} />

                    <Route path="/global-versions">
                        <GVManagementPanel globalVersions={globalVersionProps.globalVersions} />
                    </Route>

                    {isCanManageUsers && (
                        <Route path="/users/:userId?">
                            {(params) => <ManageUsersPage userId={params.userId} />}
                        </Route>
                    )}

                    {isCanManageQueries && (
                        <Route path="/manage-queries" component={QueryManagementPanel} />
                    )}

                    <Route path="/icons" component={IconGallery} />

                    <Redirect to="/documents/explore" />
                </Switch>

                <QueryWebsocketEventsManager
                    notifyOnlyOnMyQueries={location !== '/manage-queries'}
                />
            </LocalErrorBoundary>
        </React.Fragment>
    );
}
